import {
  useReorderOwnerMediaMutation,
  useUploadOwnerMediaFileMutation,
} from "@/services/media";
import { useAppDispatch } from "@app/hooks";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldFileUpload } from "@emerald-react/file-upload";
import { EmeraldNotification } from "@emerald-react/notification";
import { useSurvey } from "@hooks/useSurveySelectors";
import { AttachmentUploadsInterface } from "@shared-types/component.types";
import { useSortableList } from "@shared/hooks/useSortable";
import type { MediaFile } from "@survey-types/survey.media.types";
import { removeMedia, reorderMedia, updateMedia } from "@survey/surveySlice";
import { flattenMediaFilesWithModifiedFiles } from "@survey/utils/mediaFileUtils";
import React, { useState } from "react";
import AttachmentThumbnailPreview from "./AttachmentThumbnailPreview";

interface MediaPayload {
  surveyId: string | undefined;
  user: string | null;
  propertyId: string;
  mediaType: string;
}

interface AttachmentUploadsProps extends AttachmentUploadsInterface {
  propertyId: string;
  mediaFiles: MediaFile[];
  showUploadZone?: boolean;
}

const AttachmentUploads = ({
  fileType,
  propertyId,
  mediaFiles,
  showUploadZone = true,
  onMoveFile,
}: AttachmentUploadsProps) => {
  const [errorBool, setError] = useState(false);
  const [failedFiles, setFailedFiles] = useState<
    Array<{ fileName: string; errorMsg: string }>
  >([]);
  const [pdfError, setPdfError] = useState<string>("");
  const [uploadOwnerMediaFile] = useUploadOwnerMediaFileMutation();
  const [reorderOwnerMedia] = useReorderOwnerMediaMutation();

  const currentSurvey = useSurvey();
  const dispatch = useAppDispatch();
  const useremail = localStorage.getItem("email");
  const allMediaTypeFiles = mediaFiles;

  /**
   * Apply type-specific sorting logic as per requirements:
   * - FLIERS: Use flattening algorithm (modified files replace originals at their position)
   * - IMAGES & FLOORPLANS: Filter out originals that have modified versions, sort by order
   */
  const modifiedMediaIds = new Set(
    allMediaTypeFiles
      .filter((file) => file.modifiedFromMediaId)
      .map((file) => file.modifiedFromMediaId),
  );

  const mediaTypeFiles =
    fileType === "fliers"
      ? // For fliers: Apply flattening algorithm where modified files replace originals
        flattenMediaFilesWithModifiedFiles(allMediaTypeFiles)
      : // For images and floor plans: Filter out originals that have modified versions, then sort
        allMediaTypeFiles.filter((file) => !modifiedMediaIds.has(file.mediaId));

  const mediaType = fileType.toLocaleUpperCase();

  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/avif",
    "image/av1",
  ];

  const validPdfTypes = ["application/pdf"];
  const maxPdfFileSizeBytes = 52428800; // 50 MB in bytes

  const hideProcessingNodes = () => {
    document
      .querySelectorAll<HTMLElement>('[class="MuiGrid-root MuiGrid-item"]')
      .forEach((node) => {
        if (node.outerHTML.includes("Processing...")) {
          node.hidden = true;
        }
      });
  };

  const setUploadError = (msg: string, fileName?: string) => {
    setError(true);
    if (fileName) {
      setFailedFiles((prev) => [...prev, { fileName, errorMsg: msg }]);
    } else {
      setFailedFiles((prev) => [
        ...prev,
        { fileName: "Unknown file", errorMsg: msg },
      ]);
    }
    hideProcessingNodes();
  };

  const checkForValidDocument = async (
    file: Blob,
    fileName?: string,
  ): Promise<boolean> => {
    try {
      const text = await file.text();
      if (
        text.includes("Encrypt") ||
        text
          .substring(text.lastIndexOf("<<"), text.lastIndexOf(">>"))
          .includes("/Encrypt")
      ) {
        setUploadError(
          "The file(s) you are attempting to upload is (are) either encrypted or password protected and cannot be included in a Site IQ Survey. Please disable password protection and/or the encryption and re-upload to Site IQ. If you need further assistance, please use the chat feature to reach out to Site IQ support.",
          fileName,
        );
        return false;
      }
      return true;
    } catch {
      setUploadError("Failed to read the file.", fileName);
      return false;
    }
  };

  const isPdfFileSizeWithinLimit = (file: File): boolean => {
    if (
      file.type === "application/pdf" &&
      (fileType === "floorPlans" || fileType === "fliers") &&
      file.size > maxPdfFileSizeBytes
    ) {
      setUploadError(
        "The PDF file exceeds the 50 MB size limit. Please upload a smaller file.",
        file.name,
      );
      return false;
    }
    return true;
  };

  const checkForValidFile = async (file: File): Promise<boolean> => {
    if (!isPdfFileSizeWithinLimit(file)) {
      return false;
    }

    const isValidDocument = await checkForValidDocument(
      new Blob([file], { type: "application/pdf" }),
      file.name,
    );
    let isValidImage = true;

    if (
      file.type.includes("png") ||
      file.type.includes("jpeg") ||
      file.type.includes("jpg") ||
      file.type.includes("gif") ||
      file.type.includes("avif") ||
      file.type.includes("av1")
    ) {
      if (
        !file.name.toLowerCase().match(/\.(png|jpeg|gif|jpg|avif|av1)$/) ||
        !validImageTypes.includes(file.type)
      ) {
        setUploadError(
          "The file you are attempting to upload is either encrypted or password protected. Please try sourcing a different version without password protection, or contact the owner of this file.",
          file.name,
        );
        isValidImage = false;
      }
    }

    return isValidDocument && isValidImage;
  };

  const handleFileUpload = (
    _event:
      | React.ChangeEvent<HTMLInputElement>
      | React.DragEvent<HTMLDivElement>,
    files: File[],
  ) => {
    setError(false);
    setFailedFiles([]);
    files.forEach(async (file) => {
      const isValid = await checkForValidFile(file);
      if (isValid) {
        const payloadMedia: MediaPayload = {
          surveyId: currentSurvey?.id,
          user: useremail,
          propertyId: propertyId,
          mediaType,
        };
        const formData = new FormData();
        formData.append(
          "file",
          file,
          file.name.replace(/[^a-zA-Z0-9.\-_ ]/g, "_"),
        );
        formData.append("media", JSON.stringify(payloadMedia));
        const tempMediaId = "temp-id-" + crypto.randomUUID();
        dispatch(
          updateMedia({
            propertyId: propertyId,
            mediaType: fileType,
            media: [
              {
                mediaId: tempMediaId,
                displayName: file.name,
                mediaType: fileType,
                propertyId: propertyId,
                cloudfrontUrl: "",
                isHidden: false,
                surveyId: currentSurvey?.id || "",
                order: (allMediaTypeFiles.length || 0).toString(),
              },
            ],
          }),
        );
        uploadOwnerMediaFile(formData)
          .unwrap()
          .then((result) => {
            // Replace temp placeholder with the real uploaded file
            dispatch(
              removeMedia({
                propertyId: propertyId,
                mediaType: fileType,
                mediaId: tempMediaId,
              }),
            );
            dispatch(
              updateMedia({
                propertyId: propertyId,
                mediaType: fileType,
                media: [result],
              }),
            );
          })
          .catch((error) => {
            setUploadError(
              "File upload failed. " + (error.data?.error || ""),
              file.name,
            );
            dispatch(
              removeMedia({
                propertyId: propertyId,
                mediaType: fileType,
                mediaId: tempMediaId,
              }),
            );
            console.error("Error uploading file");
          });
      }
    });
  };

  const handleReorder = async (
    _reorderedFiles: typeof mediaTypeFiles,
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (sourceIndex === destinationIndex) return;
    const reorderedList = Array.from(mediaTypeFiles);
    let [movedItem] = reorderedList.splice(sourceIndex, 1);
    if (movedItem?.modifiedFromMediaId) {
      // If the moved item is a modified file, find the original file to move instead
      const originalIndex = Array.from(reorderedList).findIndex(
        (file) => file.mediaId === movedItem.modifiedFromMediaId,
      );
      if (originalIndex !== -1) {
        [movedItem] = reorderedList.splice(originalIndex, 1);
      }
    }
    reorderedList.splice(destinationIndex, 0, movedItem);
    dispatch(
      reorderMedia({
        propertyId: propertyId,
        mediaType: fileType,
        media: reorderedList,
      }),
    );
    if (currentSurvey?.id === undefined) return;

    // If the moved item is a modified file, update the original file's order instead
    const mediaIdToUpdate = movedItem.modifiedFromMediaId || movedItem.mediaId;

    const updatedMedia = {
      surveyId: currentSurvey?.id,
      propertyId: propertyId,
      mediaType,
      mediaId: mediaIdToUpdate,
      order: destinationIndex.toString(),
    };

    reorderOwnerMedia(updatedMedia)
      .unwrap()
      .catch((_error) => {
        console.error("Error reordering files");
      });
  };

  const acceptedFileTypes =
    fileType === "images"
      ? validImageTypes
      : fileType === "floorPlans"
        ? validImageTypes.concat(validPdfTypes)
        : validPdfTypes;

  const acceptedKeyTypes = acceptedFileTypes
    .map((type) => {
      return type
        .replace("image/", "")
        .replace("application/", "")
        .toLocaleUpperCase();
    })
    .join("/");
  const { DndContextProps, SortableContextProps, orderedItems } =
    useSortableList({
      items: mediaTypeFiles.map((file) => ({
        ...file,
        id: file.mediaId,
      })),
      onReorder: handleReorder,
      strategy: "rect",
      idField: "mediaId",
    });

  return (
    <>
      {showUploadZone && (
        <div className="flex items-start gap-x-4">
          <EmeraldFileUpload
            className="min-w-[440px]!"
            acceptedFileTypes={acceptedFileTypes}
          allowDragAndDrop={true}
          allowMultiple={true}
          uploadButtonLabel="Browse file"
          maxFileSize={52428800}
          inputLabel="Select file(s) or drop here to upload"
          inputLabelCaption={`Upto 50 MB file size and only ${acceptedKeyTypes} formats`}
          variant={"default"}
          onChange={(event, files) => {
            handleFileUpload(event, files);
          }}
          onDrop={(event, files) => {
            handleFileUpload(event, files);
          }}
        />

        {errorBool && (
          <div className="attachment-error ">
            <EmeraldNotification
              variant="error"
              title="The following file/s failed"
              message={failedFiles
                .map((file) => {
                  const lowerErrorMsg = file.errorMsg.toLowerCase();
                  const isEncryptedError =
                    lowerErrorMsg.includes("encrypted") ||
                    lowerErrorMsg.includes("password protected");
                  const isSizeError =
                    lowerErrorMsg.includes("50 mb") ||
                    lowerErrorMsg.includes("size limit");

                  const displayMessage = isEncryptedError
                    ? file.errorMsg
                    : isSizeError
                      ? "File size must be 50 MB or less."
                      : "File type is not supported";

                  return `• File: ${file.fileName}  Reason: ${displayMessage}`;
                })
                .join("\n")}
              closable
              onClose={() => {
                setError(false);
                setFailedFiles([]);
              }}
              style={{ whiteSpace: "pre-line" }}
            />
          </div>
        )}

        {pdfError && (
          <div className="attachment-error ">
            <EmeraldNotification
              variant="error"
              message={pdfError}
              closable
              onClose={() => setPdfError("")}
            />
          </div>
        )}
      </div>
      )}
      <DndContext {...DndContextProps}>
        <SortableContext {...SortableContextProps}>
          <div className="flex flex-wrap gap-6">
            {orderedItems?.map((eachFile) => {
              return (
                <AttachmentThumbnailPreview
                  eachFile={eachFile}
                  fileType={fileType}
                  key={eachFile.mediaId}
                  setPdfError={setPdfError}
                  allMediaFiles={allMediaTypeFiles}
                  onMoveFile={onMoveFile}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
};

export default AttachmentUploads;
